import posthog, { type PostHog } from 'posthog-js';

import { legacyConfig, profileConfig, type AnalyticsMode, type InitOptions, type RuntimeHooks } from './profiles';
import { environmentFromHost } from './registry/hosts';
import { LEGACY_V2_CLIENT_EVENTS } from './registry/legacy';

/**
 * Thin wrapper over the posthog-js singleton. The package reads NO environment:
 * every consumer app passes its own key/host, mode and profile.
 *
 * Modes:
 * - `live`  — initialize posthog and send events.
 * - `debug` — do NOT initialize posthog (zero network); log events to the
 *             console instead. Used locally so dev never pollutes real data.
 * - `off`   — no-op (e.g. a deployed preview with no key).
 *
 * Profile unset = today's init, byte-for-byte (Apptics builds the same
 * crm-app and never gets the profile variable): only the legacy V2 events go
 * out, and no exception, AI metric or id leaves the package.
 *
 * Every posthog entry point here is try/catch-wrapped. posthog-js runs
 * `before_send` and its `eventCaptured` listeners synchronously and neither it
 * nor the emitter catches; consumers call in from React effects and render.
 * An unswallowed throw would blank the page: analytics must never be able to
 * take the product down.
 */

export type { PostHog };

let mode: AnalyticsMode = 'off';
let started = false;
let initialized = false;
let client: PostHog | null = null;
let profiled = false;
/**
 * Whether events may leave (spec B): `clear`; `impersonated` while Clerk has an
 * `actor`; `pending` from an `awaitAuth` init until the app reports the session.
 */
let auth: 'clear' | 'impersonated' | 'pending' = 'clear';
/** A `$pageview` was dropped while `pending`: it is captured again on release. */
let pageviewHeld = false;
/**
 * Release owes the dropped `$pageview`. It is paid after a short wait, and
 * cancelled if the SDK sends a `$pageview` of its own meanwhile: auth often
 * answers with a redirect (`/` to `/sign-in`), whose history pageview lands
 * just after release and would double the count.
 */
let releasePageviewDue = false;
const RELEASE_PAGEVIEW_WAIT_MS = 250;
/** What the app itself captured while `pending`: sent on release, in order, and forgotten under an actor. */
let held: (() => void)[] = [];
const MAX_HELD = 50;

function hold(send: () => void): void {
  if (held.length < MAX_HELD) held.push(send);
}
/** `full` + `replay: 'v3'`: replay starts only once the session is known not to be impersonated. */
let replayWanted = false;
let contextGetter: (() => Record<string, unknown>) | undefined;
/** What a profiled init registered: `posthog.reset()` wipes super properties, so reset puts them back. */
let registered: Record<string, unknown> | null = null;

function readContext(): Record<string, unknown> | undefined {
  if (!contextGetter) return undefined;
  try {
    return contextGetter();
  } catch {
    return undefined;
  }
}

function blocked(): boolean {
  return auth !== 'clear';
}

const hooks: RuntimeHooks = {
  getContext: readContext,
  isImpersonated: blocked,
  onBlocked: (event) => {
    if (auth === 'pending' && event === '$pageview') pageviewHeld = true;
  },
  onPassed: (event) => {
    if (event === '$pageview') releasePageviewDue = false;
  },
  hostname: () => (typeof window === 'undefined' ? '' : window.location.hostname),
  pathname: () => (typeof window === 'undefined' ? '' : window.location.pathname),
};

/** Replaces the context getter (the provider keeps it current through a ref). */
export function setContextGetter(getter: (() => Record<string, unknown>) | undefined): void {
  contextGetter = getter;
}

function superPropertiesOf(o: InitOptions): Record<string, unknown> {
  return {
    ...(o.app ? { app: o.app } : {}),
    environment: environmentFromHost(hooks.hostname()),
    app_version: o.appVersion && o.appVersion.trim() !== '' ? o.appVersion : 'local',
    ...(o.superProperties ?? {}),
  };
}

/**
 * Initialize analytics once. The first call wins: later calls return the same
 * client and ignore their options. In `live` mode with a key it boots
 * posthog-js and returns the singleton; in `debug`/`off` mode (or `live` with
 * no key) it returns `null` and the app renders without the posthog context.
 */
export function initAnalytics(options: InitOptions): PostHog | null {
  if (initialized) return client;
  if (typeof window === 'undefined') return null;
  initialized = true;
  profiled = options.profile !== undefined;
  contextGetter = options.context;
  if (profiled && options.awaitAuth === true) auth = 'pending';
  replayWanted = options.profile === 'full' && options.replay === 'v3';

  if (options.mode === 'debug') {
    mode = 'debug';
    if (profiled) debugLog('register', superPropertiesOf(options));
    return null;
  }

  if (options.mode === 'live' && options.apiKey) {
    try {
      const config = options.profile ? profileConfig(options.profile, options, hooks) : legacyConfig(options);
      posthog.init(options.apiKey, config);
      if (options.profile) {
        registered = superPropertiesOf(options);
        posthog.register(registered);
      }
    } catch {
      mode = 'off';
      return null;
    }
    started = true;
    mode = 'live';
    client = posthog;
    return posthog;
  }

  // 'off', or 'live' with no key (e.g. a preview deploy): silent no-op.
  mode = 'off';
  return null;
}

function debugLog(action: string, ...args: unknown[]): void {
  console.log('%c[analytics]', 'color:#8b5cf6;font-weight:bold', action, ...args);
}

function run(fn: () => void): void {
  try {
    fn();
  } catch {
    // non-blocking
  }
}

const LEGACY_NAMES: ReadonlySet<string> = new Set(LEGACY_V2_CLIENT_EVENTS);

export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  // Profile unset: today's events only, every new one waits for the variable.
  if (!profiled && !LEGACY_NAMES.has(event)) return;
  if (auth === 'pending') {
    const at = new Date();
    return hold(() => {
      if (mode === 'debug') return debugLog(`capture ${event}`, { ...(readContext() ?? {}), ...(properties ?? {}) });
      if (started) run(() => posthog.capture(event, properties, { timestamp: at }));
    });
  }
  if (mode === 'debug') {
    if (blocked()) return;
    const ctx = profiled ? readContext() : undefined;
    return debugLog(`capture ${event}`, { ...(ctx ?? {}), ...(properties ?? {}) });
  }
  if (!started) return;
  run(() => {
    posthog.capture(event, properties);
  });
}

/**
 * Identify the current user. `set` is `$set` on the person, `setOnce` is
 * `$set_once`. A no-op while impersonating.
 */
export function identifyUser(
  distinctId: string,
  properties?: Record<string, unknown>,
  setOnce?: Record<string, unknown>,
): void {
  if (!distinctId || blocked()) return;
  if (mode === 'debug') return debugLog(`identify ${distinctId}`, properties ?? {}, setOnce ?? {});
  if (!started) return;
  run(() => {
    if (setOnce) posthog.identify(distinctId, properties, setOnce);
    else posthog.identify(distinctId, properties);
  });
}

/** Clear identity. MUST be called on logout. */
export function resetAnalytics(): void {
  if (mode === 'debug') return debugLog('reset');
  if (!started) return;
  run(() => {
    posthog.reset();
    if (registered) posthog.register(registered);
  });
}

/** Marks the person as internal (staff), filtered out by the test-account filter. */
export function setInternalUser(): void {
  if (blocked()) return;
  if (mode === 'debug') return debugLog('setInternalOrTestUser');
  if (!started) return;
  run(() => {
    posthog.setInternalOrTestUser();
  });
}

export function stopReplay(): void {
  if (mode === 'debug') return debugLog('stopReplay');
  if (!started) return;
  run(() => {
    posthog.stopSessionRecording();
  });
}

/**
 * Impersonated session (Clerk `actor`): an in-memory flag, never persisted
 * (unlike `opt_out_capturing()`, which would silence the support member's own
 * later sessions). While it is on, `before_send` drops every event and replay
 * stops. A reload starts clean.
 *
 * `setImpersonated(false)` is also the release of an `awaitAuth` init: the app
 * calls it once auth has loaded without an `actor`. Release starts the
 * deferred replay, sends what the app captured and reported meanwhile (with
 * their own timestamps), and captures the `$pageview` the SDK had meanwhile,
 * unless the SDK sends one itself within `RELEASE_PAGEVIEW_WAIT_MS`.
 */
export function setImpersonated(on: boolean): void {
  if (on) {
    if (auth === 'impersonated') return;
    auth = 'impersonated';
    pageviewHeld = false;
    releasePageviewDue = false;
    held = [];
    stopReplay();
    return;
  }
  if (auth === 'clear') return;
  const wasPending = auth === 'pending';
  auth = 'clear';
  if (replayWanted) startReplay();
  if (wasPending && pageviewHeld) {
    pageviewHeld = false;
    releasePageviewDue = true;
    setTimeout(() => {
      if (!releasePageviewDue) return;
      releasePageviewDue = false;
      captureEvent('$pageview');
    }, RELEASE_PAGEVIEW_WAIT_MS);
  }
  const queued = held;
  held = [];
  for (const send of queued) send();
}

function startReplay(): void {
  if (mode === 'debug') return debugLog('startReplay');
  if (!started) return;
  run(() => {
    posthog.startSessionRecording();
  });
}

/** True while impersonated, and while an `awaitAuth` init has not heard from auth yet. */
export function isImpersonated(): boolean {
  return blocked();
}

/** Captures an exception from an error boundary or a catch block. Never `capture('$exception')`. */
export function reportError(
  err: unknown,
  props: { boundary: string; route_id?: string | undefined; [k: string]: unknown },
): void {
  if (!profiled) return;
  if (auth === 'pending') return hold(() => reportError(err, props));
  if (blocked()) return;
  if (mode === 'debug') return debugLog('exception', err, props);
  if (!started) return;
  run(() => {
    posthog.captureException(err, props);
  });
}

export function captureAiMetric(traceId: string, name: string, value: string | number | boolean): void {
  if (!profiled || blocked()) return;
  if (mode === 'debug') return debugLog(`ai metric ${name}`, { traceId, value });
  if (!started) return;
  run(() => {
    posthog.captureTraceMetric(traceId, name, value);
  });
}

/** Null while the profile is unset: the ids feed new behaviour only (the Studio iframe URL). */
export function getDistinctId(): string | null {
  if (!started || !profiled) return null;
  try {
    return posthog.get_distinct_id() || null;
  } catch {
    return null;
  }
}

export function getSessionId(): string | null {
  if (!started || !profiled) return null;
  try {
    return posthog.get_session_id() || null;
  } catch {
    return null;
  }
}
